import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, ArrowRight } from "lucide-react";
import { COMPLIANCE_CORE, CONTACT } from "@/content/services";

const COLS = [
  {
    title: "Services",
    links: [
      { label: "VAPT", to: "/services/vapt" },
      { label: "Managed Security Services", to: "/services/managed-security" },
      { label: "Enterprise Landing Zone", to: "/services/enterprise-landing-zone" },
      { label: "Enterprise SIEM", to: "/services/enterprise-siem" },
      { label: "Enterprise XDR", to: "/services/enterprise-xdr" },
      { label: "Cloud Security", to: "/services/cloud-security" },
      { label: "Open Source SOC Model", to: "/services/open-source-soc" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "Industries", to: "/industries" },
      { label: "Engagement Scenarios", to: "/case-studies" },
      { label: "Insights & Blogs", to: "/insights" },
      { label: "Resources", to: "/resources" },
      { label: "Contact", to: "/contact" },
    ],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const Footer = () => (
  <footer className="border-t border-white/10 bg-[#080B13]" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-5 pt-12 sm:px-8 sm:pt-14 lg:px-16">
      <div className="glass-card flex flex-col gap-6 p-6 sm:p-10 lg:flex-row lg:items-center lg:justify-between" data-testid="footer-cta-card">
        <div>
          <h2 className="font-display text-xl font-bold text-white sm:text-2xl">Ready to secure your business?</h2>
          <p className="mt-2 max-w-md text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm">
            Get a free consultation with our experts. No obligations, just a clear assessment of your needs.
          </p>
        </div>
        <Link to="/contact" className="btn-primary w-full shrink-0 sm:w-auto lg:self-auto" data-testid="footer-cta-button">
          Get a Consultation
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>

    <div className="mx-auto max-w-7xl px-5 py-12 sm:px-8 sm:py-16 lg:px-16">
      <div className="grid gap-10 sm:gap-12 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_0.8fr_1fr]">
        <div>
          <div className="flex items-center gap-3">
            <img src="/assets/lanvaus-mark.png" alt="Lanvaus logo" width="323" height="260" className="h-9 w-auto" />
            <img src="/assets/lanvaus-wordmark.png" alt="Lanvaus" width="778" height="120" className="h-[22px] w-auto" />
          </div>
          <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-[#A6B0C9] sm:text-sm" data-testid="footer-tagline">
            Security-first cybersecurity partner for regulated industries and growing organizations. VAPT, managed SOC,
            enterprise security architecture, and open-source compliance tooling.
          </p>
          <div className="mt-6 flex gap-3">
            <a href="https://www.linkedin.com/company/lanvaus-private-limited/" target="_blank" rel="noreferrer" aria-label="Lanvaus on LinkedIn" className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-[#DDE3F2] transition-colors duration-200 hover:border-[#8E9AEA]/50 hover:text-white" data-testid="footer-social-linkedin">
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>

        {COLS.map((col) => (
          <div key={col.title}>
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#A6B0C9]">{col.title}</p>
            <ul className="mt-5 flex flex-col gap-3">
              {col.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-[#DDE3F2]/80 transition-colors duration-200 hover:text-white" data-testid={`footer-link-${slug(l.label)}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#A6B0C9]">Contact</p>
          <ul className="mt-5 flex flex-col gap-3.5 text-sm text-[#DDE3F2]/80">
            <li className="flex items-center gap-3" data-testid="footer-email">
              <Mail className="h-4 w-4 shrink-0 text-[#8E9AEA]" />
              <a href={`mailto:${CONTACT.email}`} className="transition-colors duration-200 hover:text-white">
                {CONTACT.email}
              </a>
            </li>
            {CONTACT.phones.map((p) => (
              <li key={p.value} className="flex flex-wrap items-center gap-x-3 gap-y-0.5" data-testid={`footer-phone-${slug(p.label)}`}>
                <Phone className="h-4 w-4 shrink-0 text-[#8E9AEA]" />
                <a href={p.href} className="transition-colors duration-200 hover:text-white">
                  {p.value}
                </a>
                <span className="text-[10px] uppercase tracking-wider text-[#7C86A2]">{p.label}</span>
              </li>
            ))}
            <li className="flex items-start gap-3" data-testid="footer-location">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#8E9AEA]" />
              <span className="not-italic">
                {CONTACT.addressLines.map((l) => (
                  <span key={l} className="block leading-relaxed">
                    {l}
                  </span>
                ))}
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12 flex flex-col items-start justify-between gap-5 border-t border-white/10 pt-8 sm:mt-14 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="text-xs text-[#A6B0C9]" data-testid="footer-copyright">© 2026 Lanvaus Private Limited. All rights reserved.</span>
          <Link to="/privacy" className="text-xs text-[#A6B0C9] underline-offset-4 transition-colors duration-200 hover:text-white hover:underline" data-testid="footer-privacy-link">
            Privacy Policy
          </Link>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="footer-compliance-badges">
          {COMPLIANCE_CORE.map((c) => (
            <span key={c} className="rounded-md border border-white/10 bg-white/5 px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider text-[#A6B0C9]">
              {c}
            </span>
          ))}
        </div>
      </div>
    </div>
  </footer>
);
